<script lang="ts">
							import SectionComponent from '$lib/section/SectionComponent.svelte';
							import copy from "copy-to-clipboard"; 
							import { onMount } from "svelte";
							import PrevNextSection from '$lib/prevNext/PrevNextSection.svelte';

							interface ComponentProps {
								setVisible: (id: string, v: boolean) => void;
							}
							let { setVisible }: ComponentProps = $props();
							
							const visibleById = $state<Record<string, boolean>>({});

							/**
               * This function will go through all the 'pre' elements
               * on the page and add a copy button to them.
               * Thanks to: https://slavbasharov.com/blog/adding-click-to-copy-code-markdown-blog
               */
              onMount(() => {
                  const codeBlocks = document.querySelectorAll("pre");
                  codeBlocks.forEach((block) => {
                      const copyPrompt = document.createElement("div");
                      copyPrompt.className = "copy-prompt";
                      const copyPromptText = document.createElement("p");
                      copyPromptText.innerHTML = "👆 Click to copy";
                      copyPromptText.className = 'copy-prompt-p';
                      const copyIcon = document.createElement("img");
                      copyIcon.src = "/icons/copy-icon.svg";
                      copyIcon.className = "copy-prompt-img";
                      copyPrompt.appendChild(copyIcon);
                      copyPrompt.appendChild(copyPromptText);
                      block.appendChild(copyPrompt);
											block.querySelector('.copy-prompt > p')?.addEventListener('click', () => {
												if (block.querySelector('code')?.textContent !== null && block.querySelector('code')?.textContent !== undefined) {
													copy(block.querySelector('code')!.textContent!);
												}
												if (block.querySelector('.copy-prompt > p')?.innerHTML !== null && block.querySelector('.copy-prompt > p')?.innerHTML !== undefined) {
													block.querySelector('.copy-prompt > p')!.innerHTML = 'Copied!';
													setTimeout(() => {
														block.querySelector('.copy-prompt > p')!.innerHTML = '👆 Click to copy';
													}, 1000);
												}
											});
                  });
              });
		let prevLink= '/Documentation/Typing_and_Validating/Type_Provider';
    let nextLink= '/Documentation/Interpreter_Framework';
    
</script>
<PrevNextSection {prevLink} {nextLink} />
<SectionComponent tag="h1" 
  id="validator-1"
  bind:intersecting={visibleById["validator-1"]}
  onIntersectChange={(visible) => setVisible("validator-1", visible)}
>Validator</SectionComponent>
<p>Validation defines the rules that must hold in your language. These are not syntax rules
but additional checks on the model or Abstract Syntax Tree (AST).</p>
<p>The <strong>validator</strong> definition provides the constraints or rules that should be true in the models built by your users.
Any part of the validator
definition must be included in a file with the extension <code>.valid</code>. All files with this extension in the <a
  href="/Documentation/Overview/Getting_Started#creating-a-project-2"
><em>defs</em> folder</a>
(i.e. the folder containing your definition files) are combined into one validator definition.</p>
<p>Freon supports four types of validation rules.</p>
<SectionComponent tag="h2" 
  id="simple-value-rules-2"
  bind:intersecting={visibleById["simple-value-rules-2"]}
  onIntersectChange={(visible) => setVisible("simple-value-rules-2", visible)}
>Simple Value Rules</SectionComponent>
<p>A simple value rule defines constraints on the value of a single property. Given a <a
  href="/Documentation/Creating_the_Metamodel/Defining_Properties#primitive-properties-2"
>simple property</a>,
its value can be restricted to a specified range or set of values.</p>
<pre class="language-freon"><code class="language-freon">// Insurance/src/defs/validator-extras.valid#L3-L6

EntityAttribute &#123;
    self.numVal &gt;= 12;       // in .ast file: "numVal: number;"
    self.numVal &lt;= 30;
&#125;</code></pre>
<SectionComponent tag="h2" 
  id="list-rules-3"
  bind:intersecting={visibleById["list-rules-3"]}
  onIntersectChange={(visible) => setVisible("list-rules-3", visible)}
>List Rules</SectionComponent>
<p>Given a list property, a <strong>not-empty rule</strong> can be stated. The rule is satisfied when the list
contains at least one element.</p>
<p>An <strong>is-unique rule</strong> is another rule that can be stated for a list property. The rule is satisfied
when the given property is unique within the list.</p>
<pre class="language-freon"><code class="language-freon">// Insurance/src/defs/validator-docu.valid#L3-L7

InsuranceProduct &#123;
    notEmpty self.parts;
    in self.parts isunique name;
    in self.helpers isunique name;
&#125;</code></pre>
<SectionComponent tag="h2" 
  id="valid-identifier-rules-4"
  bind:intersecting={visibleById["valid-identifier-rules-4"]}
  onIntersectChange={(visible) => setVisible("valid-identifier-rules-4", visible)}
>Valid Identifier Rules</SectionComponent>
<p>Given a simple property of type <code>identifier</code>, a <strong>valid-identifier-rule</strong> can be given. The property complies with the
rule if it is a valid identifier according to the TypeScript definition.</p>
<p>For a <em>valid-identifier-rule</em> the <code>validIdentifier</code> keyword is used followed by an optional simple property. When the
property is present, this property is checked. If the property is omitted, Freon assumes and checks a default simple property
named <code>name</code> of type <code>identifier</code>.</p>
<pre class="language-freon"><code class="language-freon">// Insurance/src/defs/validator-docu.valid#L27-L32

&#125;
CalcFunction &#123;
    // typecheck conformsTo (self.body, self.declaredType);
    in self.parameters isunique name;
    validIdentifier
    &#123;</code></pre>
<SectionComponent tag="h2" 
  id="type-check-rules-5"
  bind:intersecting={visibleById["type-check-rules-5"]}
  onIntersectChange={(visible) => setVisible("type-check-rules-5", visible)}
>Type Check Rules</SectionComponent>
<p>Based on the typing rules defined in the <a
  href="/Documentation/Typing_and_Validating/Type_Provider"
>typer</a>, rules can be stated to ensure type compliance.
To indicate a <strong>type checking rule</strong> the keyword <code>typecheck</code> is used, followed by either <code>equalsType</code> or <code>conformsTo</code>.
<strong><code>equalsType</code></strong> requires both properties to have identical types, while <strong><code>conformsTo</code></strong> allows the first to conform to the second.</p>
<p>In type checking rules it is possible to use predefined instances of a limited concept.</p>
<pre class="language-freon"><code class="language-freon">// Insurance/src/defs/validator-docu.valid#L10-L12

MinusExpression  &#123;
    typecheck equalsType( self.left, self.right );
&#125;</code></pre>
<SectionComponent tag="h2" 
  id="custom-messages-6"
  bind:intersecting={visibleById["custom-messages-6"]}
  onIntersectChange={(visible) => setVisible("custom-messages-6", visible)}
>Custom Messages</SectionComponent>
<p>Generated error messages can be customized. Similar to the editor definition,
properties can be used in the error message using the syntax <code>$&#123;propName&#125;</code>.
Optionally, a severity level can be specified. The possible values are “error”, “warning”,
“hint”, “improvement”, “todo”, and “info”. If omitted, the severity defaults to “todo”.</p>
<pre class="language-freon"><code class="language-freon">// Insurance/src/defs/validator-docu.valid#L28-L36

CalcFunction &#123;
    // typecheck conformsTo (self.body, self.declaredType);
    in self.parameters isunique name;
    validIdentifier
    &#123;
        message: "El nombre '$&#123;self.name&#125;' no es un identificador correcto.",
        severity: error
    &#125;;
&#125;</code></pre>
<SectionComponent tag="h1" 
  id="default-validation-rules-7"
  bind:intersecting={visibleById["default-validation-rules-7"]}
  onIntersectChange={(visible) => setVisible("default-validation-rules-7", visible)}
>Default Validation Rules</SectionComponent>
<p>Freon automatically applies the following default validation rules:</p>
<ul>
<li>Non-optional properties must be set.</li>
<li>Non-optional lists must contain at least one element.</li>
<li>Any reference must resolve to an existing element.</li>
</ul>


<PrevNextSection {prevLink} {nextLink} />